/**
 * A render throw anywhere below the shell used to unmount the whole tree, which
 * is how a single mismatched field between the API and `api/types.ts` turned into
 * a blank white page with no nav, no error and nothing to click. React gives no
 * partial recovery here: without a boundary the root goes.
 *
 * So the boundary sits *inside* the shell, around the route outlet only. The nav
 * and the identity block survive, the broken screen becomes one card, and the
 * user can leave. It deliberately does not try to be clever about the error —
 * it is a backstop for the bugs nobody predicted, not an error-handling strategy.
 * Expected failures are `problem+json` and go through `ErrorView`.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Changing this remounts the boundary, so navigating away clears the error. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class RouteErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(prev: Props): void {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Console is the only sink the web app has today. When an error reporter is
    // added this is its call site — and per §11.2 it must scrub before sending,
    // because a component's props can contain anything the API returned.
    console.error('[route error]', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="route-error" role="alert">
        <h1>This screen failed to render</h1>
        <p>
          Something on this page is not what the app expected, so it stopped rather than showing
          you something wrong. The rest of the app still works — use the navigation to move on.
        </p>
        <p className="muted">
          If this keeps happening, quote this when you report it: <code>{error.message}</code>
        </p>
        <button type="button" className="btn" onClick={() => this.setState({ error: null })}>
          Try this screen again
        </button>
      </div>
    );
  }
}
